<template>
  <v-dialog
      v-model="dialog"
      persistent
      :close-on-back="false"
      transition="dialog-top-transition"
  >
    <v-card rounded="lg" :loading="loading" :disabled="loading">
      <v-card-title class="mx-4 mt-4">
        <h3 class="font-weight-bold">
          {{ translation(translationList.add_basic_vehicle, 'Adaugă vehicul') }}
        </h3>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="12" md="6">
            <vehicle-registration-plate
                v-model="basicVehicleForm.registration_plate"
                @validation-vehicle-status="handleValidationVehicleStatus"
            >
            </vehicle-registration-plate>
          </v-col>
          <v-col cols="12" sm="12" md="6">
            <vin
                v-model="basicVehicleForm.vin"
                @validation-vehicle-status="handleValidationVehicleStatus"
            >
            </vin>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="12" md="6">
            <vehicle-make-select
                :vehicle-category-ws-id="basicVehicleForm.rca_mapped_category ? basicVehicleForm.rca_mapped_category.ws_id : null"
                :vehicle-category-id="basicVehicleForm.rca_mapped_category ? basicVehicleForm.rca_mapped_category.id : null"
                v-model="basicVehicleForm.make"
                @validation-vehicle-status="handleValidationVehicleStatus"
            >
            </vehicle-make-select>
          </v-col>
          <v-col cols="12" sm="12" md="6">
            <vehicle-model-select
                :vehicle-make-id="basicVehicleForm.make ? basicVehicleForm.make.id : null"
                v-model="basicVehicleForm.model"
                @validation-vehicle-status="handleValidationVehicleStatus"
            >
            </vehicle-model-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="12" md="4">
            <vehicle-max-weight
                v-model="basicVehicleForm.max_weight"
                @validation-vehicle-status="handleValidationVehicleStatus"
            >
            </vehicle-max-weight>
          </v-col>
          <v-col cols="12" sm="12" md="4">
            <vehicle-manufacture-year-select
                v-model="basicVehicleForm.manufacture_year"
                @validation-vehicle-status="handleValidationVehicleStatus"
            >
            </vehicle-manufacture-year-select>
          </v-col>
          <v-col cols="12" sm="12" md="4">
            <vehicle-number-of-seats
                v-model="basicVehicleForm.number_of_seats"
                @validation-vehicle-status="handleValidationVehicleStatus"
            >
            </vehicle-number-of-seats>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="mx-4 mb-4">
        <v-btn
            class="w-50"
            color="primary-light"
            rounded="lg"
            size="large"
            variant="outlined"
            @click="handleCloseDialog(false)"
        >
          {{ translation(translationList.dismiss, 'Renunță') }}
        </v-btn>

        <v-spacer></v-spacer>
        <v-btn
            class="w-50"
            color="primary"
            rounded="lg"
            size="large"
            text="Adauga"
            variant="flat"
            @click="handleCloseDialog(true)"
        >
          {{ translation(translationList.add, 'Adaugă') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import {defineComponent, watch, ref, onMounted} from 'vue';
import {translation, useTranslationStore} from "~/plugins/store/translations";
import {
  type ApiVehicleDetails,
  type BasicVehicle,
  createDefaultBasicVehicle,
  type ValidationBasicVehicleStatus
} from '~/types/Vehicle';
import Vin from "~/components/vehicle/Vin.vue";
import {useNuxtApp} from "#app";
import {useAuthStore} from "~/plugins/store/auth";
import {useVehicleStore} from "~/plugins/store/vehicle";
import type {TranslationList} from "~/types/Utils";
import {recaptcha} from "~/composables/recaptcha";
import {useReCaptcha} from "vue-recaptcha-v3";

export default defineComponent({
  components: {Vin},
  props: {
    showDialog: {
      type: Boolean,
      required: true,
    },
    modelValue: {
      type: Object as () => BasicVehicle | null,
      default: () => null,
      required: false
    }
  },
  setup(props, {emit}) {
    const dialog = ref(props.showDialog);
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const basicVehicleForm = ref<BasicVehicle>(createDefaultBasicVehicle());
    const {$toast} = useNuxtApp();
    const vehicleStore = useVehicleStore();
    const authStore = useAuthStore();
    const accessToken = authStore.getAccessToken();
    const recaptchaInstance = useReCaptcha();
    const validationVehicleStatus = ref(<ValidationBasicVehicleStatus>{
      registration_plate: false,
      vin: false,
      vehicle_type: true,
      rca_mapped_category: true,
      make: false,
      model: false,
      manufacture_year: false,
      max_weight: false,
      number_of_seats: false,
    });
    const loading = ref(false);

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
    });

    watch(() => props.showDialog, (newValue) => {
      if (newValue) {
        dialog.value = true;
      }
    });

    const handleCloseDialog = (response: boolean) => {
      if (response) {
        const isVehicleFormValid = (() => {
          let allValid = true;
          const vehicleFields = [
            'registration_plate',
            'rca_mapped_category',
            'vin',
            'make',
            'model',
            'manufacture_year',
            'max_weight',
            'number_of_seats'
          ];
          vehicleFields.forEach(field => {
            if (!validationVehicleStatus.value[field]) {
              window.dispatchEvent(new CustomEvent('validate-vehicle', {detail: field}));
              allValid = false;
            }
          });
          return allValid;
        })();
        if (!isVehicleFormValid) {
          $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
          return;
        }
        emit('update:modelValue', basicVehicleForm.value);
      }
      emit('response');
      dialog.value = false;
    };

    watch(() => basicVehicleForm.value.registration_plate, async (newValue, oldValue) => {
      if (newValue && newValue.length && newValue !== oldValue) {
        const token = await recaptcha(recaptchaInstance);
        loading.value = true;
        const result = await vehicleStore.fetchVehicleDetails(newValue, null, accessToken, token);
        await fillVehicleDetails(result);
        loading.value = false;
      }
    });

    const fillVehicleDetails = async (vehicleDetails: ApiVehicleDetails | null | undefined) => {
      if (!vehicleDetails) {
        // If vehicleDetails is null or undefined, return early to avoid errors
        return;
      }

      if (!basicVehicleForm.value.registration_plate && vehicleDetails.registration_plate) {
        basicVehicleForm.value.registration_plate = vehicleDetails.registration_plate;
      }
      if (!basicVehicleForm.value.vin && vehicleDetails.vin) {
        basicVehicleForm.value.vin = vehicleDetails.vin;
      }

      if (!basicVehicleForm.value.make && vehicleDetails.rca_vehicle_make) {
        basicVehicleForm.value.make = vehicleDetails.rca_vehicle_make;
      }

      if (!basicVehicleForm.value.model && vehicleDetails.rca_vehicle_model) {
        basicVehicleForm.value.model = vehicleDetails.rca_vehicle_model;
      }

      if (!basicVehicleForm.value.max_weight && vehicleDetails.max_weight) {
        basicVehicleForm.value.max_weight = vehicleDetails.max_weight;
      }

      if (!basicVehicleForm.value.number_of_seats && vehicleDetails.number_of_seats) {
        basicVehicleForm.value.number_of_seats = vehicleDetails.number_of_seats;
      }

      if (!basicVehicleForm.value.manufacture_year && vehicleDetails.manufacture_year) {
        basicVehicleForm.value.manufacture_year = parseInt(vehicleDetails.manufacture_year);
      }
    };

    const handleValidationVehicleStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      validationVehicleStatus.value[field] = isValid;
    };

    return {
      dialog,
      translationList,
      translation,
      handleCloseDialog,
      basicVehicleForm,
      handleValidationVehicleStatus,
      loading,
    };
  },
});
</script>

<style scoped>
</style>
